import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import Heading from './Heading'

const Select = ({ heading, children, onChange, selectedLabel, active, stepKey }) => {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)
  const optionsRef = useRef(null)
  const searchRef = useRef('')
  const searchTimeoutRef = useRef(null)

  const handleOptionSelect = (choice, index) => {
    setIsOpen(false)
    if (onChange) onChange(choice, index)
  }

  // Focus the options container when it opens so keydown lands there
  useEffect(() => {
    if (isOpen && optionsRef.current) {
      optionsRef.current.focus()
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Type-ahead — listen on the options container directly
  useEffect(() => {
    if (!isOpen || !optionsRef.current) return

    const handleKeyDown = (e) => {
      if (!e.key || e.key.length !== 1) return

      searchRef.current += e.key.toLowerCase()
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = setTimeout(() => {
        searchRef.current = ''
      }, 800)

      const childArray = React.Children.toArray(children)
      const match = childArray.find((child) =>
        child.props.data?.name?.toLowerCase().startsWith(searchRef.current),
      )

      if (match) {
        const matchEl = optionsRef.current?.querySelector(
          `[data-option-name="${match.props.data.name}"]`,
        )
        matchEl?.scrollIntoView({ block: 'nearest' })

        if (matchEl && optionsRef.current) {
          optionsRef.current.scrollTop = matchEl.offsetTop - optionsRef.current.offsetTop
        }
      }

      // console.log('search string:', searchRef.current)
      // console.log(
      //   'children:',
      //   childArray.map((c) => c.props.data?.name),
      // )
      // console.log('match:', match?.props.data?.name)
      // console.log(
      //   'matchEl:',
      //   optionsRef.current?.querySelector(
      //     `[data-option-name="${match?.props.data?.name}"]`,
      //   ),
      // )
    }

    const el = optionsRef.current
    el.addEventListener('keydown', handleKeyDown)
    return () => el.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, children])

  return (
    // Remove tabIndex from the wrapper
    <SelectWrapper className="select-wrapper" ref={wrapperRef} $active={active}  data-step={stepKey}>
      <Heading size="13px" align="left">
        {heading}
      </Heading>
      <Display onClick={() => setIsOpen(!isOpen)}>
        {selectedLabel || 'Select...'}
        <Arrow>{isOpen ? '▲' : '▼'}</Arrow>
      </Display>

      {isOpen && (
        // tabIndex here so it can receive focus and keydown events
        <OptionsContainer ref={optionsRef} tabIndex={-1}>
          {React.Children.map(children, (child, index) =>
            React.cloneElement(child, {
              onSelect: () => handleOptionSelect(child.props.data, index),
            }),
          )}
        </OptionsContainer>
      )}
    </SelectWrapper>
  )
}

export default Select

const Display = styled.div`
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  cursor: pointer;
  background: var(--input-bg);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
`

const SelectWrapper = styled.div`
  opacity: ${(props) => (props.$active ? 1 : 0.6)};
  pointer-events: ${(props) => (props.$active ? 'auto' : 'none')};
  position: relative;
  width: 100%;
  font-family: sans-serif;
  color: var(--text-color);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-transform: uppercase;


  @keyframes stepPulse {
  0%   { box-shadow: 0 0 0 0px #ff0000ff; }
  50%  { box-shadow: 0 0 0 4px #ff0000cc; }
  100% { box-shadow: 0 0 0 0px #ff000000; }
  }

 &.step-highlight ${Display} {
    animation: stepPulse 1.5s ease-out infinite;
    border-radius: 8px;
  }
`

const OptionsContainer = styled.div`
  border-radius: 8px;
  background: var(--input-bg);
  position: absolute;
  border: 1px solid var(--border-soft);
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  top: 28px;
`

const Arrow = styled.span`
  font-size: 0.8em;
`
